import { buildQmsDocumentDownloadUrl } from "../qms/qmsDocumentDownloadUtils";

import utils from "../../../utils/utils";

export function normalizeWorksheetDisplayName(name) {
  const value = String(name || "").trim().toLowerCase();
  if (!value) return "";
  const lastDot = value.lastIndexOf(".");
  if (lastDot <= 0) return value;
  return value.slice(0, lastDot);
}

export function getWorksheetFileExtension(name, url = "") {
  const fromName = String(name || "").trim();
  const nameDot = fromName.lastIndexOf(".");
  if (nameDot > 0) {
    return fromName.slice(nameDot + 1).toLowerCase();
  }
  const fromUrl = String(url || "").trim().split("?")[0];
  const urlDot = fromUrl.lastIndexOf(".");
  if (urlDot > 0) {
    return fromUrl.slice(urlDot + 1).toLowerCase();
  }
  return "pdf";
}

export function isDuplicateWorksheetName(name, existingRows = [], options = {}) {
  const normalized = normalizeWorksheetDisplayName(name);
  if (!normalized) return false;
  const excludeId = options.excludeStudyWorkSheetId || null;
  const excludeGroupId = options.excludeStudyWorkSheetGroupId || null;

  return (existingRows || []).some((row) => {
    const rowId = row.StudyWorkSheetId || row.studyWorkSheetId;
    const rowGroupId =
      row.StudyWorkSheetGroupId || row.studyWorkSheetGroupId || null;
    if (excludeId && rowId === excludeId) return false;
    if (excludeGroupId && rowGroupId === excludeGroupId) return false;
    const rowName = normalizeWorksheetDisplayName(
      row.WorkSheetName || row.workSheetName,
    );
    return rowName === normalized;
  });
}

export function getWorksheetFileIconClass(name, url = "") {
  const ext = getWorksheetFileExtension(name, url);
  switch (ext) {
    case "pdf":
      return "fas fa-file-pdf is-pdf";
    case "doc":
    case "docx":
      return "fas fa-file-word is-word";
    case "xls":
    case "xlsx":
      return "fas fa-file-excel is-excel";
    case "ppt":
    case "pptx":
      return "fas fa-file-powerpoint is-ppt";
    default:
      return "fas fa-file-alt is-generic";
  }
}

export function worksheetHasOlderVersions(row) {
  if (!row) return false;
  const flag = row.OlderVersion ?? row.olderVersion;
  return flag === 1 || flag === "1" || flag === true;
}

export function getWorksheetUploadInfo(row) {
  if (!row) {
    return { uploader: "", uploadedOn: "", shortText: "", tooltip: "" };
  }

  const uploader = String(row.CreatedBy || row.createdBy || "").trim();
  const uploadedOnRaw = row.SystemDateTime || row.systemDateTime || "";
  const uploadedOn = uploadedOnRaw
    ? utils.formatDateTimeDisplay(uploadedOnRaw)
    : "";
  const version = row.VersionNumber ?? row.versionNumber ?? "";
  const name = String(row.WorkSheetName || row.workSheetName || "").trim();
  const isLocked = !!(row.IsLocked ?? row.isLocked);
  const lockedReason = String(row.LockedReason || row.lockedReason || "").trim();

  const shortParts = [];
  if (uploader) shortParts.push(uploader);
  if (uploadedOn && uploadedOn !== "—") shortParts.push(uploadedOn);
  const shortText = shortParts.join(" · ");

  const tooltipParts = [];
  if (name) tooltipParts.push(name);
  if (version !== "" && version != null) tooltipParts.push(`Version ${version}`);
  if (uploader) tooltipParts.push(`Uploaded by ${uploader}`);
  if (uploadedOn && uploadedOn !== "—") tooltipParts.push(uploadedOn);
  if (isLocked) {
    tooltipParts.push(lockedReason ? `Locked: ${lockedReason}` : "Locked");
  }
  const tooltip = tooltipParts.join(" · ");

  return { uploader, uploadedOn, shortText, tooltip };
}

/** @deprecated Use getWorksheetUploadInfo(row).shortText */
export function formatWorksheetUploadMeta(row) {
  return getWorksheetUploadInfo(row).shortText;
}

export function buildWorksheetPreviewModel(row) {
  if (!row) return null;
  const name = row.WorkSheetName || row.workSheetName || "Document";
  const url = row.WorkSheetURL || row.workSheetUrl || "";
  const version = row.VersionNumber || row.versionNumber || 1;
  const extension = getWorksheetFileExtension(name, url);

  return {
    documentUrl: buildQmsDocumentDownloadUrl(url),
    documentExtension: extension,
    documentTitle: normalizeWorksheetDisplayName(name) || name,
    documentCode: `Version ${version}`,
    documentId: row.StudyWorkSheetId || row.studyWorkSheetId || null,
    uploadedBy: row.CreatedBy || row.createdBy || "",
    uploadedOn: row.SystemDateTime || row.systemDateTime || "",
    version,
    studyWorkSheetGroupId:
      row.StudyWorkSheetGroupId || row.studyWorkSheetGroupId || null,
    studyWorkSheetId: row.StudyWorkSheetId || row.studyWorkSheetId || null,
    hasOlderVersions: worksheetHasOlderVersions(row),
    row,
  };
}
